const DEFAULT_API_BASE = '/financehelp/app/api';

function normalizeBase(value) {
  const base = typeof value === 'string' && value.trim()
    ? value.trim()
    : DEFAULT_API_BASE;
  return base.replace(/\/+$/, '');
}

export const API_BASE = normalizeBase(import.meta.env.VITE_API_BASE);

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function requestUrl(path) {
  const suffix = String(path || '');
  return `${API_BASE}${suffix.startsWith('/') ? suffix : `/${suffix}`}`;
}

function isNativeBody(value) {
  return (
    typeof value === 'string' ||
    (typeof Blob !== 'undefined' && value instanceof Blob) ||
    (typeof FormData !== 'undefined' && value instanceof FormData) ||
    (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) ||
    (typeof ArrayBuffer !== 'undefined' && (
      value instanceof ArrayBuffer ||
      ArrayBuffer.isView(value)
    ))
  );
}

async function readBody(response) {
  if (response.status === 204 || response.status === 205) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Cookie-authenticated JSON client for the isolated Karat service.
 */
export async function api(path, options = {}) {
  const {
    body: suppliedBody,
    headers: suppliedHeaders,
    json,
    ...suppliedOptions
  } = options;
  const headers = new Headers(suppliedHeaders);
  let body = json === undefined ? suppliedBody : json;

  if (body != null && typeof body === 'object' && !isNativeBody(body)) {
    body = JSON.stringify(body);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const response = await fetch(requestUrl(path), {
    cache: 'no-store',
    ...suppliedOptions,
    headers,
    body,
    // WHY: the service authenticates web requests with an HttpOnly cookie.
    credentials: 'include',
  });
  const responseBody = await readBody(response);

  if (!response.ok) {
    const message = (
      responseBody &&
      typeof responseBody === 'object' &&
      (responseBody.error || responseBody.message)
    ) || `Request failed (${response.status})`;
    throw new ApiError(message, response.status, responseBody);
  }

  return responseBody;
}

export const apiGet = (path, options = {}) => api(path, {
  ...options,
  method: 'GET',
});

export const apiPost = (path, body, options = {}) => api(path, {
  ...options,
  method: 'POST',
  body,
});

export const apiPut = (path, body, options = {}) => api(path, {
  ...options,
  method: 'PUT',
  body,
});

export default api;
